import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useAtomValue } from 'jotai'
import { styled } from '@mui/material/styles'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import { fgProductsAtom, fgProductsV2Atom } from '@/core/feelgreat/feelgreat.store'
import type { V1CartEntry } from '@/core/cart/useCartPricing'
import { omanFormatDataAtom, isLoadingOmanFormatAtom } from '../../../stores/oman.store'
import { v2CartAtom } from '@/core/cart/cart.store'
import { checkoutReferralInfoAtom } from '@/core/checkout/checkout.store'
import { hotFormatRawAtom } from '@/core/store/hotFormat.store'
import { localize } from '@/core/utils'
import BinIconSvg from '@/core/assets/BinIcon.svg?react'
import TagIconSvg from '@/core/assets/TagIcon.svg?react'
import { useOmanCartActions } from '../../../hooks/useOmanCartActions'
import { useOmanOrderPricing } from '../../../hooks/useOmanOrderPricing'
import { getProductPrice, usePriceMode } from '@/core/cart/useCart'
import type { FGProduct, FGProducts, FGProductV2 } from '@/core/feelgreat/feelgreat.types'
import type { V2CartItem } from '@/core/cart/cart.store'

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENCY = 'OMR'

const COLORS = {
    navy: '#003764',
    darkBlue: '#153862',
    green: '#009ECF',
    badge: '#17A550',
    freeShipping: '#FF820E',
    muted: '#64748b',
    bgLight: '#e9f1fa',
    bgSavings: '#f0f6fc',
    border: '#e9f0f7',
    qtyMinusDisabled: '#99BCDF'
} as const

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatPrice = (amount: number) => {
    if (!amount) return '-'
    return `${amount.toFixed(2)} ${CURRENCY}`
}

const findProductByItemCode = (products: FGProducts, itemCode: string) => {
    for (const flavors of Object.values(products)) {
        for (const product of Object.values(flavors)) {
            if (product.item_code === itemCode) return product
        }
    }
    return undefined
}

// Look up price from fgProducts by item_code + type so we use the correct
// member/retail price per purchase type instead of the raw server value.
const resolveFormatItemPrice = (
    products: FGProducts,
    itemCode: string,
    type: 'subscription' | 'onetime' | undefined,
    priceMode: 'member' | 'retail',
    fallback: number
): number => {
    const purchaseType = type === 'subscription' ? 'subscribe' : 'onetime'
    for (const [flavor, purchaseTypes] of Object.entries(products)) {
        if (purchaseTypes[purchaseType]?.item_code === itemCode) {
            return getProductPrice(products, flavor, purchaseType, priceMode)
        }
    }
    return fallback
}

const resolveProductName = (product: FGProduct | undefined) =>
    localize(product?.flavor?.titleInOrderSummary) || localize(product?.flavor?.text) || 'Feel Great Pack'

const resolveV2NameByItemCode = (productsV2: FGProductV2[], itemCode: string): string => {
    for (const product of productsV2) {
        for (const variant of product.variants) {
            const optionItem = variant.optionItems.find(oi => oi.item_code === itemCode)
            if (optionItem) return localize(optionItem.item_name) || localize(product.name)
        }
    }
    return 'Feel Great Pack'
}

const resolveSubItems = (product: FGProduct | undefined) => product?.flavor?.subItems?.map(s => localize(s))

type FormatItemInput = Array<{ item_code: string; qty: number; type?: 'subscription' | 'onetime' }>

const computeFormatMemberDiscountV2 = (productsV2: FGProductV2[], items: FormatItemInput): number => {
    return items.reduce((total, item) => {
        for (const product of productsV2) {
            for (const variant of product.variants) {
                const optionItem = variant.optionItems.find(oi => oi.item_code === item.item_code)
                if (!optionItem) continue
                const before = optionItem.price.before ?? 0
                const member = optionItem.price.member ?? optionItem.price.retail
                return total + Math.max(0, before - member) * item.qty
            }
        }
        return total
    }, 0)
}

const computeV2CartMemberDiscount = (v2CartRaw: V2CartItem[], productsV2: FGProductV2[]): number => {
    return v2CartRaw.reduce((total, item) => {
        const product = productsV2.find(p => p.product_id === item.product_id)
        if (!product) return total
        const variant = product.variants.find(v => Object.keys(v.combination).every(k => item.selection[k] === v.combination[k]))
        if (!variant) return total
        const optionItem = variant.optionItems.find(oi => oi.option === item.purchase_option)
        if (!optionItem) return total
        const before = optionItem.price.before ?? 0
        const member = optionItem.price.member ?? optionItem.price.retail
        return total + Math.max(0, before - member) * item.qty
    }, 0)
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const BinIcon = ({ onClick }: { onClick?: () => void }) => (
    <BinIconSvg
        width={24}
        height={24}
        style={{ flexShrink: 0, cursor: onClick ? 'pointer' : 'default' }}
        onClick={onClick}
        data-testid="pre-summary-box-bin-icon-svg-on-click"
    />
)

const TagIcon = () => <TagIconSvg width={14} height={14} style={{ flexShrink: 0 }} data-testid="pre-summary-box-tag-icon-svg" />

interface QtyControlsProps {
    qty: number
    onIncrease?: () => void
    onDecrease?: () => void
    readOnly?: boolean
}

const QtyControls = ({ qty, onIncrease, onDecrease, readOnly }: QtyControlsProps) => {
    const canDecrease = qty > 1
    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap="8px"
            sx={{
                bgcolor: COLORS.bgLight,
                borderRadius: '20px',
                minWidth: readOnly ? '32px' : '65px',
                height: '28px',
                px: readOnly ? '10px' : 0,
                opacity: readOnly ? 0.5 : 1
            }}
            data-testid="pre-summary-box-stack-typography-1">
            {!readOnly && (
                <Typography
                    variant="body2"
                    fontSize="14px"
                    sx={{
                        color: canDecrease ? COLORS.navy : COLORS.qtyMinusDisabled,
                        cursor: canDecrease ? 'pointer' : 'default',
                        userSelect: 'none',
                        px: '4px'
                    }}
                    onClick={canDecrease ? onDecrease : undefined}
                    data-testid="pre-summary-box-typography-1">
                    -
                </Typography>
            )}
            <Typography variant="body2" fontSize="14px" fontWeight={600} sx={{ color: COLORS.navy }} data-testid="pre-summary-box-typography-2">
                {qty}
            </Typography>
            {!readOnly && (
                <Typography
                    variant="body2"
                    fontSize="14px"
                    sx={{ color: COLORS.navy, cursor: 'pointer', userSelect: 'none', px: '4px' }}
                    onClick={onIncrease}
                    data-testid="pre-summary-box-typography-on-increase">
                    +
                </Typography>
            )}
        </Stack>
    )
}

interface CartItemRowProps {
    name: string
    subItems?: string[]
    qty: number
    unitPrice: number
    imageUrl?: string
    isSubscribe: boolean
    onIncrease?: () => void
    onDecrease?: () => void
    onRemove?: () => void
    readOnly?: boolean
}

const CartItemRow = ({ name, subItems, qty, unitPrice, imageUrl, isSubscribe, onIncrease, onDecrease, onRemove, readOnly }: CartItemRowProps) => {
    const { translate } = useTranslate()
    return (
        <ItemStack data-testid="pre-summary-box-item-stack">
            <Stack direction="row" gap="12px" width="100%" data-testid="pre-summary-box-stack-qty-controls">
                {imageUrl && (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={name}
                        sx={{ width: 70, height: 70, objectFit: 'contain', borderRadius: '8px', flexShrink: 0 }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.style.display = 'none'
                        }}
                        data-testid="pre-summary-box-box-1"
                    />
                )}
                <Stack flex={1} minWidth={0} spacing="10px" data-testid="pre-summary-box-stack-typography-2">
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap="8px" data-testid="pre-summary-box-stack-typography-3">
                        <Stack flex={1} minWidth={0} spacing="4px" data-testid="pre-summary-box-stack-typography-4">
                            <Typography variant="body2" fontSize="14px" fontWeight={700} sx={{ color: COLORS.navy }} data-testid="pre-summary-box-typography-3">
                                {name}
                            </Typography>
                            {subItems?.map((item, i) => (
                                <Typography
                                    key={i}
                                    variant="body2"
                                    fontSize="12px"
                                    sx={{ color: COLORS.muted, lineHeight: 1.6 }}
                                    data-testid="pre-summary-box-typography-4">
                                    {item}
                                </Typography>
                            ))}
                        </Stack>
                        <Stack alignItems="flex-end" flexShrink={0} data-testid="pre-summary-box-stack-typography-5">
                            <Stack direction="row" alignItems="center" gap="6px" data-testid="pre-summary-box-stack-typography-6">
                                <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    fontSize="14px"
                                    sx={{ color: COLORS.navy }}
                                    data-testid="pre-summary-box-typography-5">
                                    {formatPrice(unitPrice * qty)}
                                </Typography>
                                {!readOnly && <BinIcon onClick={onRemove} data-testid="pre-summary-box-bin-icon-on-remove" />}
                            </Stack>
                        </Stack>
                    </Stack>

                    <Stack direction="row" alignItems="center" justifyContent="space-between" data-testid="pre-summary-box-stack-typography-7">
                        <Stack direction="row" alignItems="center" gap="6px" data-testid="pre-summary-box-stack-typography-8">
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: COLORS.badge }} data-testid="pre-summary-box-box-2" />
                            <Typography
                                variant="body2"
                                fontSize="11px"
                                fontWeight={400}
                                sx={{ color: COLORS.badge }}
                                data-testid="pre-summary-box-typography-6">
                                {isSubscribe ? (
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: `${formatPrice(unitPrice)} ${translate('subsctiption_per_bill')}`
                                        }}
                                        data-testid="pre-summary-box-span"
                                    />
                                ) : (
                                    translate('ontime_per_bill')
                                )}
                            </Typography>
                        </Stack>
                        <QtyControls qty={qty} onIncrease={onIncrease} onDecrease={onDecrease} readOnly={readOnly} data-testid="pre-summary-box-qty-controls" />
                    </Stack>
                </Stack>
            </Stack>
        </ItemStack>
    )
}

const DiscountRow = ({ label, amount }: { label: string; amount: number }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" data-testid="pre-summary-box-stack-typography-9">
        <Typography variant="body2" fontSize="14px" fontWeight={400} sx={{ color: COLORS.navy }} data-testid="pre-summary-box-typography-7">
            {label}
        </Typography>
        <Stack direction="row" alignItems="center" gap="4px" data-testid="pre-summary-box-stack-tag-icon">
            <TagIcon data-testid="pre-summary-box-tag-icon" />
            <Typography variant="body2" fontSize="12px" fontWeight={600} sx={{ color: COLORS.green }} data-testid="pre-summary-box-typography-8">
                -{amount.toFixed(2)} {CURRENCY}
            </Typography>
        </Stack>
    </Stack>
)

const SavingsSection = ({
    v1Entries,
    hasV1Cart,
    formatMemberDiscount = 0,
    referredBy = ''
}: {
    v1Entries: V1CartEntry[]
    hasV1Cart: boolean
    formatMemberDiscount?: number
    referredBy?: string
}) => {
    const { translate } = useTranslate()
    const hotFormatRaw = useAtomValue(hotFormatRawAtom)
    const u23DiscountValue = Number(hotFormatRaw?.ushop?.u23?.formatTotal?.discount?.value ?? 0)

    const products = v1Entries.map(e => e.product).filter(Boolean)
    const subscriptionDiscount = hasV1Cart ? products.reduce((sum, p) => sum + (p?.discount?.subscription ?? 0), 0) : 0

    if (subscriptionDiscount <= 0 && u23DiscountValue <= 0 && formatMemberDiscount <= 0 && !referredBy) return null

    return (
        <Stack
            sx={{ bgcolor: COLORS.bgSavings, borderRadius: '10px', p: '16px', mt: '16px' }}
            spacing="10px"
            data-testid="pre-summary-box-stack-typography-your-savings">
            <Typography variant="body2" fontSize="14px" fontWeight={700} sx={{ color: COLORS.navy }} data-testid="pre-summary-box-typography-your-savings">
                {translate('your_savings')}
            </Typography>
            {formatMemberDiscount > 0 && (
                <>
                    <DiscountRow label={translate('member_discount')} amount={formatMemberDiscount} data-testid="pre-summary-box-discount-row-format-member" />
                    {referredBy && (
                        <Stack mt="5px !important" direction="row" alignItems="center" data-testid="pre-summary-box-referred-by">
                            <Typography
                                variant="body2"
                                fontSize="10px"
                                fontWeight={400}
                                sx={{ color: '#6687a2' }}
                                data-testid="pre-summary-box-referred-by-label">
                                {translate('referred_by')} {referredBy}
                            </Typography>
                        </Stack>
                    )}
                </>
            )}
            {subscriptionDiscount > 0 && (
                <DiscountRow label={translate('subscription_discount')} amount={subscriptionDiscount} data-testid="pre-summary-box-discount-row-2" />
            )}
            {u23DiscountValue > 0 && <DiscountRow label={translate('discount')} amount={u23DiscountValue} data-testid="pre-summary-box-discount-row-u23" />}
        </Stack>
    )
}

// ─── Main component ───────────────────────────────────────────────────────────

type OmanCartActions = ReturnType<typeof useOmanCartActions>

const PreSummaryBox = ({ isLoading = false, readOnly = false, cartActions }: { isLoading?: boolean; readOnly?: boolean; cartActions?: OmanCartActions }) => {
    const { translate } = useTranslate()
    const formatData = useAtomValue(omanFormatDataAtom)
    const fgProducts = useAtomValue(fgProductsAtom)
    const fgProductsV2 = useAtomValue(fgProductsV2Atom)
    const v2CartRaw = useAtomValue(v2CartAtom)
    const isLoadingFormat = useAtomValue(isLoadingOmanFormatAtom)

    const noopVoid = () => {
        /* no-op */
    }
    // V1 methods are dead code for Oman (always V2) but kept as noops so the
    // V1 render path in renderItems() compiles without casting.
    const increaseV1Qty = (_pt: string, _f: string) => false
    const decreaseV1Qty = (_pt: string, _f: string) => false
    const removeV1Entry = (_pt: string, _f: string) => false
    const {
        increaseV2Qty = noopVoid,
        decreaseV2Qty = noopVoid,
        changeFormatItemQty = noopVoid,
        removeV2Entry = noopVoid,
        removeFormatItem = noopVoid,
        orderSpecError = null
    } = cartActions ?? {}

    const priceMode = usePriceMode()
    const { v1Entries, v2CartEntries, hasV1Cart, hasV2Cart, hasFormatItems, subtotal, freight, productCredit, total } = useOmanOrderPricing()

    const referralInfo = useAtomValue(checkoutReferralInfoAtom)

    const formatMemberDiscount = (() => {
        if (hasV2Cart) return computeV2CartMemberDiscount(v2CartRaw, fgProductsV2)
        if (!hasFormatItems || !formatData?.items) return 0
        return computeFormatMemberDiscountV2(fgProductsV2, formatData.items)
    })()

    const hasData = hasFormatItems || hasV1Cart || hasV2Cart

    const navigate = useNavigate()
    // Editable summary only: once the cart is emptied (total qty 0, e.g. the
    // last item removed), send the user back to shop. Skipped in readOnly mode
    // and while loading, so a transient empty state during fetch never fires it.
    useEffect(() => {
        if (readOnly || isLoading || isLoadingFormat) return
        if (!hasData) navigate('/products', { replace: true })
    }, [readOnly, isLoading, isLoadingFormat, hasData, navigate])

    if (isLoading || isLoadingFormat) return null

    const renderItems = () => {
        if (hasFormatItems) {
            return formatData!.items!.map(item => {
                const p = findProductByItemCode(fgProducts, item.item_code)
                return (
                    <CartItemRow
                        key={`${item.type ?? 'x'}-${item.item_code}`}
                        name={resolveV2NameByItemCode(fgProductsV2, item.item_code)}
                        subItems={resolveSubItems(p)}
                        qty={item.qty}
                        unitPrice={resolveFormatItemPrice(fgProducts, item.item_code, item.type, priceMode, item.price)}
                        imageUrl={p?.image_url}
                        isSubscribe={item.type === 'subscription'}
                        onIncrease={() => changeFormatItemQty(item.item_code, 1, item.qty, item.type)}
                        onDecrease={() => changeFormatItemQty(item.item_code, -1, item.qty, item.type)}
                        onRemove={() => removeFormatItem(item.item_code, item.type)}
                        readOnly={readOnly}
                        data-testid="pre-summary-box-cart-item-row-1"
                    />
                )
            })
        }

        if (hasV2Cart && v2CartEntries.length > 0) {
            return v2CartEntries.map((entry, i) => (
                <CartItemRow
                    key={`v2-${i}`}
                    name={entry.name}
                    qty={entry.qty}
                    unitPrice={entry.unitPrice}
                    imageUrl={entry.imageUrl}
                    isSubscribe={entry.isSubscribe}
                    onIncrease={() => increaseV2Qty(i)}
                    onDecrease={() => decreaseV2Qty(i)}
                    onRemove={() => removeV2Entry(i)}
                    readOnly={readOnly}
                    data-testid="pre-summary-box-cart-item-row-2"
                />
            ))
        }

        return v1Entries.map((entry, i) => (
            <CartItemRow
                key={`${entry.purchaseType}-${entry.product?.flavor?.value ?? i}`}
                name={resolveProductName(entry.product)}
                subItems={resolveSubItems(entry.product)}
                qty={entry.qty}
                unitPrice={entry.unitPrice}
                imageUrl={entry.product?.image_url}
                isSubscribe={entry.purchaseType === 'subscribe'}
                onIncrease={() => increaseV1Qty(entry.purchaseType, entry.flavor)}
                onDecrease={() => decreaseV1Qty(entry.purchaseType, entry.flavor)}
                onRemove={() => removeV1Entry(entry.purchaseType, entry.flavor)}
                readOnly={readOnly}
                data-testid="pre-summary-box-cart-item-row-3"
            />
        ))
    }

    return (
        <Card sx={{ boxSizing: 'border-box', borderRadius: '12px', width: '100%' }} data-testid="pre-summary-box-card-card-content-styled">
            <CardContentStyled data-testid="pre-summary-box-card-content-styled">
                <Stack direction="row" justifyContent="space-between" alignItems="center" data-testid="pre-summary-box-stack-typography-order-summary">
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        fontSize="18px"
                        sx={{ color: COLORS.navy }}
                        data-testid="pre-summary-box-typography-order-summary">
                        {translate('order_summary')}
                    </Typography>
                </Stack>

                {orderSpecError && (
                    <Alert severity="warning" sx={{ mt: 1, fontSize: 13 }} data-testid="pre-summary-box-alert">
                        {orderSpecError}
                    </Alert>
                )}

                {hasData && <Stack data-testid="pre-summary-box-stack">{renderItems()}</Stack>}

                <Stack pt="30px" gap="12px" data-testid="pre-summary-box-stack-typography-subtotal-1">
                    <Stack direction="row" justifyContent="space-between" data-testid="pre-summary-box-stack-typography-subtotal-2">
                        <Typography variant="caption" fontSize="14px" sx={{ color: COLORS.navy }} data-testid="pre-summary-box-typography-subtotal">
                            {translate('subtotal')}
                        </Typography>
                        <Typography variant="caption" fontSize="14px" sx={{ color: COLORS.darkBlue }} data-testid="pre-summary-box-typography-9">
                            {formatPrice(subtotal)}
                        </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" data-testid="pre-summary-box-stack-typography-shipping">
                        <Typography variant="caption" fontSize="14px" sx={{ color: COLORS.navy }} data-testid="pre-summary-box-typography-shipping">
                            {translate('shipping')}
                        </Typography>
                        <Typography
                            variant="caption"
                            fontSize="14px"
                            sx={{ color: freight === 0 ? COLORS.freeShipping : COLORS.darkBlue }}
                            data-testid="pre-summary-box-typography-10">
                            {freight === 0 ? translate('free') : formatPrice(freight)}
                        </Typography>
                    </Stack>

                    {productCredit > 0 && (
                        <Stack direction="row" justifyContent="space-between" data-testid="pre-summary-box-stack-typography-product-credit">
                            <Typography variant="caption" fontSize="14px" sx={{ color: COLORS.navy }} data-testid="pre-summary-box-typography-product-credit">
                                {translate('product_credit')}
                            </Typography>
                            <Typography variant="caption" fontSize="14px" sx={{ color: COLORS.green }} data-testid="pre-summary-box-typography-11">
                                - {formatPrice(productCredit)}
                            </Typography>
                        </Stack>
                    )}
                </Stack>

                <Stack
                    mt="24px"
                    mb="10px"
                    pt="24px"
                    direction="row"
                    justifyContent="space-between"
                    sx={{ borderTop: `1px solid ${COLORS.border}` }}
                    data-testid="pre-summary-box-stack-typography-total-price">
                    <Typography fontWeight={700} fontSize="16px" sx={{ color: COLORS.navy }} data-testid="pre-summary-box-typography-total-price">
                        {translate('total_price')}
                    </Typography>
                    <Typography fontWeight={700} fontSize="16px" sx={{ color: COLORS.navy }} data-testid="pre-summary-box-typography-12">
                        {formatPrice(total)}
                    </Typography>
                </Stack>

                <SavingsSection
                    v1Entries={v1Entries}
                    hasV1Cart={hasV1Cart}
                    formatMemberDiscount={formatMemberDiscount}
                    referredBy={referralInfo?.displayName ?? ''}
                    data-testid="pre-summary-box-savings-section"
                />
            </CardContentStyled>
        </Card>
    )
}

// ─── Styled ───────────────────────────────────────────────────────────────────

const CardContentStyled = styled(CardContent)`
    padding: 20px;
    @media screen and (min-width: 600px) {
        padding: 30px;
    }
    @media screen and (min-width: 1200px) {
        padding: 20px 20px 30px 20px;
    }
`

const ItemStack = styled(Stack)`
    padding: 24px 0;
    &:not(:last-of-type) {
        border-bottom: 1px solid ${COLORS.border};
    }
`

export default PreSummaryBox
